/**
 * Builds a numeric-id → string lookup map from a list of entities.
 *
 * Entities without a numeric `id`, or whose value field is not a string, are
 * skipped so callers can safely index with `map[id] ?? fallback`.
 *
 * @param entities - Source collection (e.g. store entities).
 * @param valueKey - Property whose string value becomes the map entry.
 * @returns A plain `Record<number, string>` keyed by entity `id`.
 * @example
 * toLookupById(countries, 'name') // { 784: 'United Arab Emirates', … }
 * toLookupById(countries, 'code') // { 784: 'AE', … }
 */
export function toLookupById<T extends { id: number }>(
  entities: readonly T[],
  valueKey: keyof T & string
): Record<number, string> {
  const map: Record<number, string> = {};
  for (const entity of entities) {
    const value = entity[valueKey];
    if (typeof value === 'string') {
      map[entity.id] = value;
    }
  }
  return map;
}
